import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useUsers } from "../hooks/useUsers";
import { Button } from "../../../../ui/components/Button";
import { TextField } from "../../../../ui/components/TextField";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";
import {
  FeatherArrowLeft,
  FeatherUser,
  FeatherMail,
  FeatherX,
  FeatherPlus,
} from "@subframe/core";

export const UserUpdatePage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { getUser, updateUser, loading, error } = useUsers();
  const [formData, setFormData] = useState({
    email: "",
    userName: "",
    is_active: true,
  });
  const [originalFormData, setOriginalFormData] = useState({
    email: "",
    userName: "",
    is_active: true,
  });
  const [assignedCompanies, setAssignedCompanies] = useState<
    Array<{
      id: number;
      company_name: string;
      app_url: string;
      role_name: string | null;
    }>
  >([]);
  const [originalAssignedCompanyIds, setOriginalAssignedCompanyIds] = useState<
    number[]
  >([]);
  const [availableCompanies, setAvailableCompanies] = useState<
    Array<{
      id: number;
      company_name: string;
      app_url: string;
    }>
  >([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loadingData, setLoadingData] = useState(true);

  const loadUserData = useCallback(async () => {
    if (!id) return;
    try {
      setLoadingData(true);
      const data = await getUser(Number(id));
      const loadedFormData = {
        email: data.user.email,
        userName: data.user.userName || "",
        is_active: data.user.is_active ?? true,
      };
      setFormData(loadedFormData);
      setOriginalFormData(loadedFormData);
      setAssignedCompanies(data.assigned_companies);
      setOriginalAssignedCompanyIds(
        data.assigned_companies.map((c) => c.id)
      );
      setAvailableCompanies(
        data.available_companies.map((c) => ({
          id: c.id,
          company_name: c.company_name,
          app_url: c.app_url,
        }))
      );
    } catch (err: any) {
      alert(err.message || "Failed to load user data");
      navigate("/admin/dashboard?tab=users");
    } finally {
      setLoadingData(false);
    }
  }, [getUser, id, navigate]);

  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleAssignCompany = (companyId: number) => {
    const company = availableCompanies.find((c) => c.id === companyId);
    if (company) {
      setAssignedCompanies((prev) => [
        ...prev,
        { ...company, role_name: null },
      ]);
      setAvailableCompanies((prev) =>
        prev.filter((c) => c.id !== companyId)
      );
    }
  };

  const handleUnassignCompany = (companyId: number) => {
    const company = assignedCompanies.find((c) => c.id === companyId);
    if (company) {
      setAssignedCompanies((prev) => prev.filter((c) => c.id !== companyId));
      setAvailableCompanies((prev) => [
        ...prev,
        { id: company.id, company_name: company.company_name, app_url: company.app_url },
      ]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    const newErrors: Record<string, string> = {};
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }
    if (!formData.userName.trim()) {
      newErrors.userName = "Username is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      // Compute diff: what was added and what was removed
      const currentAssignedIds = assignedCompanies.map((c) => c.id);
      const assignedCompanyIds = currentAssignedIds.filter(
        (id) => !originalAssignedCompanyIds.includes(id)
      );
      const unassignedCompanyIds = originalAssignedCompanyIds.filter(
        (id) => !currentAssignedIds.includes(id)
      );

      const updateData: any = {};
      const email = formData.email.trim();
      const userName = formData.userName.trim();

      if (email !== originalFormData.email) {
        updateData.email = email;
      }
      if (userName !== originalFormData.userName) {
        updateData.userName = userName;
      }
      if (formData.is_active !== originalFormData.is_active) {
        updateData.is_active = formData.is_active ? 1 : 0;
      }

      // Only include assignment arrays if there are changes
      if (assignedCompanyIds.length > 0) {
        updateData.assigned_company_ids = assignedCompanyIds;
      }
      if (unassignedCompanyIds.length > 0) {
        updateData.unassigned_company_ids = unassignedCompanyIds;
      }

      await updateUser(Number(id), updateData);
      navigate("/admin/dashboard?tab=users");
    } catch (err: any) {
      alert(err.message || "Failed to update user");
    }
  };

  if (loadingData) {
    return (
      <DefaultPageLayout>
        <div className="flex w-full h-full items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading user data...</p>
          </div>
        </div>
      </DefaultPageLayout>
    );
  }

  return (
    <DefaultPageLayout>
      <div className="flex h-full w-full flex-col gap-6 p-12">
        <div className="flex w-full items-center gap-4">
          <Button
            variant="neutral-secondary"
            icon={<FeatherArrowLeft />}
            onClick={() => navigate("/admin/dashboard?tab=users")}
          >
            Back
          </Button>
          <span className="text-heading-2 font-heading-2 text-default-font">
            Update User
          </span>
        </div>

        <div className="flex w-full flex-col items-center justify-center">
          {error && (
            <div className="flex w-full max-w-2xl flex-col items-start gap-2 p-4 bg-red-50 border border-red-200 rounded-lg mb-6">
              <span className="text-sm font-medium text-red-800">
                {error.message}
              </span>
            </div>
          )}

          <form
            onSubmit={handleSubmit}
            className="flex w-full max-w-2xl flex-col items-start gap-6"
          >
            <div className="flex w-full flex-col gap-4">
              <TextField
                label="Email"
                helpText={errors.email || ""}
                error={!!errors.email}
                icon={<FeatherMail />}
              >
                <TextField.Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter email"
                  disabled={loading}
                />
              </TextField>

              <TextField
                label="Username"
                helpText={errors.userName || ""}
                error={!!errors.userName}
                icon={<FeatherUser />}
              >
                <TextField.Input
                  name="userName"
                  value={formData.userName}
                  onChange={handleChange}
                  placeholder="Enter username"
                  disabled={loading}
                />
              </TextField>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="is_active"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="h-4 w-4"
                  disabled={loading}
                />
                <label
                  htmlFor="is_active"
                  className="text-body font-body text-default-font cursor-pointer"
                >
                  Active
                </label>
              </div>

              {/* Assigned Companies */}
              <div className="flex flex-col gap-2">
                <label className="text-caption-bold font-caption-bold text-default-font text-left">
                  Assigned Companies
                </label>
                <div className="flex flex-col gap-2 max-h-64 overflow-y-auto border border-solid border-neutral-border rounded-md p-4">
                  {assignedCompanies.length === 0 ? (
                    <span className="text-body font-body text-subtext-color">
                      No companies assigned
                    </span>
                  ) : (
                    assignedCompanies.map((company) => (
                      <div
                        key={company.id}
                        className="flex items-center justify-between gap-2 p-2 border border-solid border-neutral-border rounded"
                      >
                        <span className="text-body font-body text-default-font">
                          {company.company_name}
                          {company.role_name && (
                            <span className="ml-2 text-xs text-subtext-color">
                              ({company.role_name})
                            </span>
                          )}
                        </span>
                        <Button
                          type="button"
                          variant="destructive-tertiary"
                          size="small"
                          icon={<FeatherX />}
                          onClick={() => handleUnassignCompany(company.id)}
                          disabled={loading}
                        >
                          Remove
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Available Companies */}
              {availableCompanies.length > 0 && (
                <div className="flex flex-col gap-2">
                  <label className="text-caption-bold font-caption-bold text-default-font text-left">
                    Available Companies
                  </label>
                  <div className="flex flex-col gap-2 max-h-64 overflow-y-auto border border-solid border-neutral-border rounded-md p-4">
                    {availableCompanies.map((company) => (
                      <div
                        key={company.id}
                        className="flex items-center justify-between gap-2 p-2 border border-solid border-neutral-border rounded"
                      >
                        <span className="text-body font-body text-default-font">
                          {company.company_name}
                        </span>
                        <Button
                          type="button"
                          variant="neutral-secondary"
                          size="small"
                          icon={<FeatherPlus />}
                          onClick={() => handleAssignCompany(company.id)}
                          disabled={loading}
                        >
                          Assign
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-4">
                <Button
                  type="submit"
                  disabled={loading}
                  loading={loading}
                  className="w-auto"
                >
                  Update User
                </Button>
                <Button
                  type="button"
                  variant="neutral-secondary"
                  onClick={() => navigate("/admin/dashboard?tab=users")}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </DefaultPageLayout>
  );
};

